import { Observable, of, defer, merge } from "rxjs"
import { switchMap, catchError, filter, delayWhen } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { compact } from "lodash"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    FETCH_CASH_REPORT_REQUESTED,
    fetchCashReportFailure,
    fetchCashReportRequested,
    fetchCashReportSuccess,
} from "../../../../api/actions"
import { getHttpHeaders } from "../../../auth/selectors"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import { CASH_REPORT_AGENDA_MOUNTED, cashReportAgendaMounted, clearCashReportData } from "./actions"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_CASH_REPORT_DEFAULT_FILTER,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_PAGINATION,
    setCashReportDefaultFilter,
    setPagination,
    setTotalCount,
} from "../../../filters/actions"
import type { Action, StateObject } from "../../../redux/types"
import {
    getCashReportCurrentPage,
    getCashReportItemsByPageNumber,
    getCashReportQueryApiParam,
    isCashReportAgendaOpened,
} from "./selectors"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../customers/customersTable/epics"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { fetchCashReportData } from "./request"

export const cashReportAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CASH_REPORT_AGENDA_MOUNTED),
        switchMap(() => {
            return [clearCashReportData(), setCashReportDefaultFilter()]
        })
    )

export const fetchCashReportOnAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isCashReportAgendaOpened(deps.getState())),
        switchMap(() => [cashReportAgendaMounted()])
    )

export const nextCashReportPageRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_PAGINATION),
        filter((action: Action) => action.payload.identifier === "cash"),
        switchMap(action => {
            const pageToGo = action.payload.paginationValue.currentPage

            if (!pageToGo) {
                return []
            }

            const pageData = getCashReportItemsByPageNumber(deps.getState(), pageToGo)
            return !pageData || pageData.length === 0 ? [fetchCashReportRequested()] : []
        })
    )

export const cashReportDefaultFilterEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_CASH_REPORT_DEFAULT_FILTER),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        filter(() => isCashReportAgendaOpened(deps.getState())),
        switchMap(() => {
            return [
                clearCashReportData(),
                setPagination("cash", {
                    currentPage: 1,
                }),
            ]
        })
    )

export const cashReportFilterChanged = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "cash" || action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isCashReportAgendaOpened(deps.getState())),
        switchMap(() => {
            return [
                clearCashReportData(),
                setPagination("cash", {
                    currentPage: 1,
                }),
            ]
        })
    )

export const cashReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CASH_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isCashReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("cash")])
    )

export const fetchCashReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CASH_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isCashReportAgendaOpened(deps.getState())),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            return defer(() =>
                fetchCashReportData(getHttpHeaders(deps.getState()), getCashReportQueryApiParam(deps.getState()))
            ).pipe(
                switchMap((result): any => {
                    if (result && result.data) {
                        return [
                            createLoadingEnded("cash"),
                            setTotalCount("cash", result.totalCount),
                            fetchCashReportSuccess(compact(result.data), getCashReportCurrentPage(deps.getState())),
                        ]
                    }

                    return [fetchCashReportFailure(), createLoadingEnded("cash")]
                }),
                catchError(() => of(createLoadingEnded("cash")))
            )
        })
    )

export default [
    cashReportDefaultFilterEpic,
    cashReportLoadingStartedEpics,
    cashReportAgendaMountedEpics,
    fetchCashReportOnAccountSwitch,
    nextCashReportPageRequestedEpics,
    cashReportFilterChanged,
    fetchCashReportRequestedEpics,
]
